"use client";

/**
 * CtaStepsOverviewPanel
 * Home screen of the multi-step CTA dialog for a returning guest: one row per step with a status
 * pill and a one-line summary. Rows open their step. A guest who declined but still holds a room
 * gets an inline "Keep my room / Release room" choice on the hotel row (never a dialog on top of
 * the dialog); a room the guest released themself can be restored from its row, and any other
 * cancelled booking offers "Book again".
 */

import { memo } from "react";
import { Check, ChevronRight } from "lucide-react";
import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchCallout } from "@/fetch-ui/FetchCallout";
import type { CtaHotelStepOverview, CtaStepsOverview } from "../../_logic/cta-steps";
import {
  describeOverviewStep,
  findContinueIndex,
  hasDeclinedStep,
  type OverviewPillTone,
} from "../../_logic/cta-steps-overview-display";

/** What a guest can do with a booking from its overview row. Owned by the dialog so it persists. */
export interface OverviewRoomActions {
  /** A booking with a release/restore request in flight (by response id). */
  pendingBookingId: number | null;
  /** Bookings the guest chose to keep after declining, so the callout doesn't keep asking. */
  keptBookingIds: ReadonlySet<number>;
  onKeep: (step: CtaHotelStepOverview) => void;
  onRelease: (step: CtaHotelStepOverview) => void;
  onRestore: (step: CtaHotelStepOverview) => void;
}

interface CtaStepsOverviewPanelProps {
  overview: CtaStepsOverview;
  /** Right after finishing every step: show "You're all set". */
  allSet: boolean;
  onOpenStep: (index: number) => void;
  onDone: () => void;
  roomActions: OverviewRoomActions;
}

/** Pill colors; -700 text on -50 backgrounds keeps small text above 4.5:1. */
const PILL_CLASS: Record<OverviewPillTone, string> = {
  success: "bg-green-50 text-green-700",
  info: "bg-indigo-50 text-indigo-700",
  warning: "bg-amber-50 text-amber-800",
  danger: "bg-red-50 text-red-700",
  neutral: "bg-gray-100 text-gray-700",
};

/**
 * Renders the steps overview.
 * @param props - See {@link CtaStepsOverviewPanelProps}
 * @returns The overview
 * @throws Never
 */
export const CtaStepsOverviewPanel = memo(function CtaStepsOverviewPanel({
  overview,
  allSet,
  onOpenStep,
  onDone,
  roomActions,
}: CtaStepsOverviewPanelProps): JSX.Element {
  // Derived values
  const { pendingBookingId, keptBookingIds, onKeep, onRelease, onRestore } = roomActions;
  const hasDeclined = hasDeclinedStep(overview);
  const continueIndex = findContinueIndex(overview);
  const continueStep = continueIndex === null ? undefined : overview.steps[continueIndex];

  // Render
  return (
    <div className="flex flex-col gap-4">
      {allSet && (
        <div className="flex flex-col items-center gap-2 py-2 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50">
            <Check className="h-6 w-6 text-green-700" strokeWidth={2.5} aria-hidden="true" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">You&apos;re all set</h3>
        </div>
      )}

      <ol className="flex flex-col divide-y divide-gray-100 rounded-xl border border-gray-200">
        {overview.steps.map((step, index) => {
          const display = describeOverviewStep(step, hasDeclined);
          const hotelStep = step.kind === "hotel-booking" ? step : null;
          const isPending = hotelStep?.responseId != null && hotelStep.responseId === pendingBookingId;
          const showReleasePrompt =
            display.stillBooked && hotelStep?.responseId != null && !keptBookingIds.has(hotelStep.responseId);
          const isCancelled = hotelStep?.status === "cancelled" && !hasDeclined;
          const showRestore = isCancelled && hotelStep.canRestore;
          const showBookAgain = isCancelled && !hotelStep.canRestore && display.openable;
          const rowContent = (
            <>
              <span
                aria-hidden="true"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-medium text-gray-700"
              >
                {index + 1}
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-sm font-medium text-gray-900">{step.name}</span>
                {display.summary && <span className="text-xs font-medium text-gray-600">{display.summary}</span>}
              </span>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${PILL_CLASS[display.tone]}`}>
                {display.pill}
              </span>
              {display.openable && <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />}
            </>
          );

          return (
            <li key={`${step.kind}-${index}`} className="flex flex-col gap-2 p-1">
              {display.openable ? (
                <button
                  type="button"
                  onClick={() => onOpenStep(index)}
                  className="flex w-full touch-manipulation items-center gap-3 rounded-lg px-3 py-3 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  aria-label={`Step ${index + 1}, ${step.name}: ${display.pill}. Open`}
                >
                  {rowContent}
                </button>
              ) : (
                <div className="flex w-full items-center gap-3 px-3 py-3">{rowContent}</div>
              )}

              {showReleasePrompt && hotelStep && (
                <div className="px-3 pb-2">
                  <FetchCallout variant="warning">
                    <div className="flex flex-col gap-3">
                      <p className="text-sm font-medium">
                        You&apos;re not attending but still have a room at {step.name}
                        {display.summary ? `, ${display.summary}` : ""}.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <FetchButton variant="primary" disabled={isPending} onClick={() => onKeep(hotelStep)}>
                          Keep my room
                        </FetchButton>
                        <FetchButton variant="outline" loading={isPending} onClick={() => onRelease(hotelStep)}>
                          Release room
                        </FetchButton>
                      </div>
                    </div>
                  </FetchCallout>
                </div>
              )}

              {showRestore && hotelStep && (
                <div className="px-3 pb-2">
                  <FetchButton variant="outline" loading={isPending} onClick={() => onRestore(hotelStep)}>
                    Restore your room
                  </FetchButton>
                </div>
              )}

              {showBookAgain && (
                <div className="px-3 pb-2">
                  <FetchButton variant="outline" onClick={() => onOpenStep(index)}>
                    Book again
                  </FetchButton>
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <div className="flex flex-col gap-2">
        {continueStep && continueIndex !== null && (
          <FetchButton variant="primary" onClick={() => onOpenStep(continueIndex)} className="w-full">
            Continue to {continueStep.name}
          </FetchButton>
        )}
        <FetchButton variant={continueStep ? "outline" : "primary"} onClick={onDone} className="w-full">
          Done
        </FetchButton>
      </div>
    </div>
  );
});

CtaStepsOverviewPanel.displayName = "CtaStepsOverviewPanel";
